import { COLORS } from '../../IndividualPlan/tokens';
import type { PeCohortBaseline, PeCohortDomainAverage, ScoreUnit } from '../types';

type CohortBaselinePanelProps = {
  cohortLabel: string;
  qualifier?: string;
  baseline: PeCohortBaseline | null;
  framingText: string;
  unit: ScoreUnit;
  absent?: boolean;
};

function signed(value: number, decimals = 0): string {
  const fixed = value.toFixed(decimals);
  return value > 0 ? `+${fixed}` : fixed;
}

function asPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function yoyFraming(change: number | null): string | null {
  if (change === null) return null;
  if (change === 0) return 'unchanged vs last year';
  return `${signed(change)} vs last year`;
}

function domainAverage(row: PeCohortDomainAverage, unit: ScoreUnit, cohort: boolean): string {
  if (unit === 'proficiency_level') {
    const value = cohort ? row.cohort_proficiency_level : row.non_cohort_proficiency_level;
    return value === null ? '—' : value.toFixed(1);
  }
  const value = cohort ? row.cohort_scale_score : row.non_cohort_scale_score;
  return value === null ? '—' : String(value);
}

const CARD_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 13,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${COLORS.divider}`,
  borderRadius: 8,
  padding: 20,
  overflow: 'hidden',
};

const HEADER_STYLE = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 8,
  width: '100%',
};

const HEADER_LABEL_STYLE = {
  margin: 0,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 700,
  color: COLORS.ink,
};

const HEADER_QUALIFIER_STYLE = {
  flexShrink: 0,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 700,
  color: COLORS.muted,
  textAlign: 'right' as const,
  whiteSpace: 'nowrap' as const,
};

const HEADLINE_STYLE = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 11,
};

const HEADLINE_VALUE_STYLE = {
  fontSize: 32,
  lineHeight: '40px',
  fontWeight: 800,
  letterSpacing: '-0.5px',
  color: COLORS.ink,
};

const HEADLINE_META_STRONG_STYLE = {
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 700,
  color: COLORS.muted,
};

const HEADLINE_META_REGULAR_STYLE = {
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 500,
  color: COLORS.muted,
};

const METRICS_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 13,
};

const ENTRY_PANEL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 42,
  margin: 0,
  backgroundColor: COLORS.bg,
  borderRadius: 8,
  padding: '8px 16px',
};

const ENTRY_STAT_STYLE = {
  flex: '1 0 0',
  display: 'flex',
  flexDirection: 'column' as const,
  minWidth: 0,
};

const ENTRY_LABEL_STYLE = {
  margin: 0,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 700,
  color: COLORS.muted,
  textTransform: 'uppercase' as const,
};

const ENTRY_VALUE_STYLE = {
  margin: 0,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 700,
  color: COLORS.ink,
};

const ENTRY_DIVIDER_STYLE = {
  flexShrink: 0,
  alignSelf: 'stretch',
  width: 1,
  backgroundColor: '#FFFFFF',
};

const TABLE_STYLE = {
  width: '100%',
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
  border: `1px solid ${COLORS.divider}`,
  borderRadius: 8,
  overflow: 'hidden',
};

const CELL_BASE_STYLE = {
  padding: '8px 16px',
  fontSize: 16,
  lineHeight: '24px',
  color: COLORS.ink,
  textAlign: 'left' as const,
  borderBottom: `1px solid ${COLORS.divider}`,
};

const FIRST_COL_STYLE = { paddingLeft: 24 };
const COHORT_COL_STYLE = { width: 72 };
const NON_COHORT_COL_STYLE = { width: 96 };
const LAST_ROW_STYLE = { borderBottom: 'none' };

const FRAMING_STYLE = {
  margin: 0,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 500,
  color: COLORS.muted,
};

const SMALL_N_STYLE = {
  margin: 0,
  fontSize: 13,
  lineHeight: '18px',
  fontWeight: 500,
  color: COLORS.muted,
};

const ZERO_STATE_STYLE = {
  margin: 0,
  fontSize: 16,
  lineHeight: '24px',
  color: COLORS.muted,
};

export function CohortBaselinePanel({
  cohortLabel,
  qualifier,
  baseline,
  framingText,
  unit,
  absent = false,
}: CohortBaselinePanelProps) {
  if (absent || baseline === null) return null;

  if (baseline.cohort_size === 0) {
    return (
      <section className="cohort-baseline-panel" style={CARD_STYLE}>
        <p className="cohort-zero-state" style={ZERO_STATE_STYLE}>
          {`0 ${cohortLabel} in your data this year`}
        </p>
      </section>
    );
  }

  const yoy = yoyFraming(baseline.year_over_year_change);
  const hasEntrySplit =
    typeof baseline.district_grown_count === 'number' &&
    typeof baseline.newly_entered_count === 'number';
  const domainRows = baseline.domain_averages ?? [];

  return (
    <section className="cohort-baseline-panel" style={CARD_STYLE}>
      <div className="cohort-baseline-header" style={HEADER_STYLE}>
        <h4 className="cohort-baseline-label" style={HEADER_LABEL_STYLE}>
          {cohortLabel}
        </h4>
        {qualifier && (
          <span
            className="cohort-baseline-qualifier"
            data-testid="cohort-baseline-qualifier"
            style={HEADER_QUALIFIER_STYLE}
          >
            {qualifier}
          </span>
        )}
      </div>

      <div className="cohort-headline" data-testid="cohort-headline" style={HEADLINE_STYLE}>
        <span
          className="cohort-headline-value"
          data-testid="cohort-headline-value"
          style={HEADLINE_VALUE_STYLE}
        >
          {baseline.cohort_size}
        </span>
        <span className="cohort-headline-meta" style={HEADLINE_META_STRONG_STYLE}>
          {asPercent(baseline.percent_of_program)} of program
        </span>
        {yoy && (
          <span className="cohort-headline-meta" style={HEADLINE_META_REGULAR_STYLE}>
            {yoy}
          </span>
        )}
      </div>

      <div data-testid="cohort-metrics" className="cohort-metrics" style={METRICS_STYLE}>
        {hasEntrySplit && (
          <dl className="cohort-entry-split" data-testid="cohort-entry-split" style={ENTRY_PANEL_STYLE}>
            <div className="cohort-entry-stat" style={ENTRY_STAT_STYLE}>
              <dt style={ENTRY_LABEL_STYLE}>District-grown</dt>
              <dd style={ENTRY_VALUE_STYLE}>{baseline.district_grown_count}</dd>
            </div>
            <span aria-hidden="true" className="cohort-entry-divider" style={ENTRY_DIVIDER_STYLE} />
            <div className="cohort-entry-stat" style={ENTRY_STAT_STYLE}>
              <dt style={ENTRY_LABEL_STYLE}>Newly-entered</dt>
              <dd style={ENTRY_VALUE_STYLE}>{baseline.newly_entered_count}</dd>
            </div>
          </dl>
        )}

        {domainRows.length > 0 && (
          <table className="cohort-domain-table" data-testid="cohort-domain-table" style={TABLE_STYLE}>
            <thead>
              <tr>
                <th style={{ ...CELL_BASE_STYLE, ...FIRST_COL_STYLE, fontWeight: 700 }}>
                  Domain
                </th>
                <th style={{ ...CELL_BASE_STYLE, ...COHORT_COL_STYLE, fontWeight: 700 }}>
                  Cohort Avg.
                </th>
                <th style={{ ...CELL_BASE_STYLE, ...NON_COHORT_COL_STYLE, fontWeight: 700 }}>
                  All Others Avg.
                </th>
              </tr>
            </thead>
            <tbody>
              {domainRows.map((row, index) => {
                const last = index === domainRows.length - 1 ? LAST_ROW_STYLE : null;
                return (
                  <tr key={row.domain}>
                    <td style={{ ...CELL_BASE_STYLE, ...FIRST_COL_STYLE, fontWeight: 500, ...last }}>
                      {row.domain}
                    </td>
                    <td style={{ ...CELL_BASE_STYLE, ...COHORT_COL_STYLE, fontWeight: 500, ...last }}>
                      {domainAverage(row, unit, true)}
                    </td>
                    <td
                      style={{ ...CELL_BASE_STYLE, ...NON_COHORT_COL_STYLE, fontWeight: 500, ...last }}
                    >
                      {domainAverage(row, unit, false)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {baseline.small_n_caveat && (
        <p data-testid="small-n-caveat" className="cohort-small-n" style={SMALL_N_STYLE}>
          Fewer than 10 students — interpret with caution.
        </p>
      )}
      {framingText !== '' && (
        <p className="cohort-framing" style={FRAMING_STYLE}>
          {framingText}
        </p>
      )}
    </section>
  );
}
